import { digitsFaToEn } from '@persian-tools/persian-tools';
import moment from 'jalali-moment';
import { withApproxTimePrefix } from '@/modules/booking/utils/withApproxTimePrefix';

const TEHRAN_TZ = 'Asia/Tehran';

const getTehranMinuteParts = (ms: number) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TEHRAN_TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));

  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value ?? 0);

  return { minute: read('minute'), second: read('second') };
};

/** Patient-facing online-visit display: the real time, ceiled to a 5-minute Tehran clock (no added buffer). */
export const applyOnlineVisitPatientDisplayBufferMs = (ms: number): number => {
  let t = ms;
  const { minute, second } = getTehranMinuteParts(t);
  const msInSecond = ((t % 1000) + 1000) % 1000;
  const onFiveMinuteMark = minute % 5 === 0 && second === 0 && msInSecond === 0;
  if (onFiveMinuteMark) return t;

  const rem = minute % 5;
  const minutesToAdd = rem === 0 ? 5 : 5 - rem;
  t = t - second * 1000 - msInSecond;
  return t + minutesToAdd * 60 * 1000;
};

const parseBookTimeToMs = (timeText: string): number | null => {
  const latin = digitsFaToEn(timeText).trim();
  let body = latin.replace(/^حدود\s+/, '').trim();
  body = body.replace(/^ساعت\s+/, '').trim();

  const dateThenClock = body.match(/^(\d{4}\/\d{2}\/\d{2})\s+(\d{1,2}:\d{2})$/);
  if (dateThenClock) {
    const parsed = moment.from(`${dateThenClock[1]} ${dateThenClock[2]}`, 'fa', 'JYYYY/JMM/JDD HH:mm');
    return parsed?.isValid() ? parsed.valueOf() : null;
  }

  const clockThenDate = body.match(/^(\d{1,2}:\d{2})\s*-\s*(\d{4}\/\d{2}\/\d{2})$/);
  if (clockThenDate) {
    const parsed = moment.from(`${clockThenDate[2]} ${clockThenDate[1]}`, 'fa', 'JYYYY/JMM/JDD HH:mm');
    return parsed?.isValid() ? parsed.valueOf() : null;
  }

  // Anything else is not a time we understand: the caller keeps the text as it is. (moment.from is
  // lenient and used to turn e.g. «بزودی» into «حدود 00:15 - 0000/01/01».)
  return null;
};

/** Receipt / my-appointments book_time strings → «حدود HH:mm - YYYY/MM/DD» ceiled to 5 minutes. */
export const formatOnlineVisitPatientBookTime = (timeText: string | null | undefined): string => {
  if (timeText == null) return '';
  const trimmed = String(timeText).trim();
  if (!trimmed) return '';

  const ms = parseBookTimeToMs(trimmed);
  if (ms == null) return withApproxTimePrefix(trimmed);

  const display = moment(applyOnlineVisitPatientDisplayBufferMs(ms)).locale('fa');
  return `حدود ${display.format('HH:mm')} - ${display.format('jYYYY/jMM/jDD')}`;
};
